/**
 * Pure briefing logic, shared by the two panels that render a briefing: the
 * docked/drawer `LumioChat` and the full-screen `LumioMobile`.
 *
 * Those two have genuinely different chrome (the mobile overlay has its own
 * animated header, backdrop and composer), so they do not share markup. What
 * they must not diverge on is the rules: which nudges may dock, and how the
 * proposals digest reads. Keeping those here means a change lands on both
 * surfaces at once, and can be tested without rendering either.
 */
import type { LumioNudge, PendingProposal } from './endpoint';

/** How many proposal titles the digest previews before it counts the rest. */
const PREVIEW_COUNT = 2;

/**
 * May this nudge be docked into a briefing?
 *
 * `pending_decisions` is excluded: the Needs-you card already owns proposals,
 * so docking it too would say the same thing twice in one briefing.
 */
export const isDockableNudge = (nudge: LumioNudge | null): boolean =>
  Boolean(nudge && nudge.id !== 'pending_decisions');

/**
 * The Needs-you subtitle: the first proposal titles, then a count of the rest,
 * so the digest hints at what is waiting without opening the inbox.
 *
 * `more` renders the overflow count, passed in by the caller because it is
 * translated copy and this module stays free of i18n and React.
 */
export const proposalsPreview = (
  proposals: readonly PendingProposal[],
  more: (count: number) => string,
): string => {
  const preview = proposals
    .slice(0, PREVIEW_COUNT)
    .map((proposal) => proposal.title)
    .join(' · ');
  const extra = proposals.length - PREVIEW_COUNT;
  return extra > 0 ? `${preview} · ${more(extra)}` : preview;
};
